import { createFileRoute } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { getIntegrationProvider } from '@/pages/integrations/providers'
import { IntegrationType } from '@deepguide-ai/types'
import { AlertCircle, CheckCircle, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

export const Route = createFileRoute('/(auth)/auth-callback/$provider')({
  component: IntegrationCallback,
})

function IntegrationCallback() {
  const params = Route.useParams()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading'
  )
  const [message, setMessage] = useState('Processing authorization...')
  const isAuthHandled = useRef<boolean>(false)

  // Get the URL parameters
  const urlParams = new URLSearchParams(window.location.search)
  const code = urlParams.get('code')
  const state = urlParams.get('state')
  const error = urlParams.get('error')
  const providerName = params.provider ?? 'Unknown'
  const providerKey = providerName.toUpperCase() as IntegrationType
  const provider = getIntegrationProvider(providerKey)

  // On component mount, exchange the code for a token
  useEffect(() => {
    if (isAuthHandled.current) {
      return; // already handled, use a ref to avoid re-running the effect
    }

    // Check if user cancelled the authorization
    if (error) {
      isAuthHandled.current = true

      let errorMessage = 'Authorization failed'
      if (error === 'access_denied') {
        errorMessage = 'Authorization cancelled'
        setMessage('You cancelled the authorization. You can try again anytime.')
      } else {
        errorMessage = `Authorization error: ${error}`
        setMessage(errorMessage)
      }

      setStatus('error')

      // Notify opener window about the cancellation/error
      if (window.opener) {
        try {
          window.opener.postMessage(
            {
              type: 'INTEGRATION_AUTH_ERROR',
              provider: providerName,
              error: errorMessage,
            },
            window.location.origin
          )
        } catch (e) {
          console.log('Could not communicate error to opener window:', e)
        }
      }

      // Auto close the window after a short delay
      setTimeout(() => {
        window.close()
      }, 2000)

      return
    }

    // Handle auth completion
    const handleAuth = async () => {
      if (!code || !state) {
        setStatus('error')
        setMessage('Missing required parameters for authorization.')
        return
      }

      // Get config for current provider

      if (!provider) {
        setStatus('error')
        setMessage(`Unsupported provider: ${providerName}`)
        return
      }

      try {
        console.log('handleAuth', { code, state })
        // Call the provider-specific completion handler
        let result = undefined
        if (provider.onConnectComplete) {
          result = await provider.onConnectComplete({ code, state })
          console.log('handleAuth result', result)
        }

        setStatus('success')
        setMessage(`${provider.name} integration connected successfully!`)

        // Notify opener window if it exists
        if (window.opener) {
          try {
            // Send generic success message
            window.opener.postMessage(
              {
                type: 'INTEGRATION_AUTH_SUCCESS',
                provider: providerName,
                success: true,
                result,
              },
              window.location.origin
            )
          } catch (e) {
            console.log('Could not communicate with opener window:', e)
          }
        }

        // Auto close the window after a short delay
        // setTimeout(() => {
        //   window.close()
        // }, 2000)
      } catch (error) {
        setStatus('error')
        // Extract more detailed error message if possible
        let errorMessage = 'Unknown error occurred'

        if (error instanceof Error) {
          errorMessage = error.message
        } else if (typeof error === 'object' && error !== null) {
          // Try to extract error details from TRPC error response
          const trpcError = error as {
            data?: {
              zodError?: unknown
              message?: string
            }
            message?: string
            error?: string
          }

          if (trpcError.data?.zodError) {
            errorMessage = 'Invalid data format'
          } else if (trpcError.message) {
            errorMessage = trpcError.message
          } else if (trpcError.data?.message) {
            errorMessage = trpcError.data.message
          } else if (trpcError.error) {
            errorMessage = trpcError.error
          }
        }

        console.error(`${provider.name} integration error:`, error)
        setMessage(`Error connecting ${provider.name}: ${errorMessage}`)

        // Notify opener window about the error
        if (window.opener) {
          try {
            window.opener.postMessage(
              {
                type: 'INTEGRATION_AUTH_ERROR',
                provider: providerName,
                error: errorMessage,
              },
              window.location.origin
            )
          } catch (e) {
            console.log('Could not communicate error to opener window:', e)
          }
        }
      }
    }

    if (code && state && provider && !isAuthHandled.current) {
      isAuthHandled.current = true
      handleAuth()
    }
  }, [code, state, provider, providerName, isAuthHandled])

  // Handle manually closing the window
  const handleClose = () => {
    window.close()
  }

  // Get display name for the provider
  const providerDisplayName = provider?.name || capitalize(providerName)

  // Helper function to capitalize provider name
  function capitalize(str: string): string {
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase()
  }

  return (
    <div className='bg-background flex min-h-screen w-full items-center justify-center'>
      <div className='w-full max-w-md rounded-lg p-8 text-center'>
        {status === 'loading' && (
          <div className='flex flex-col items-center'>
            <Loader2 className='text-primary mb-4 h-16 w-16 animate-spin' />
            <h2 className='mb-4 text-2xl font-bold'>
              Connecting {providerDisplayName}
            </h2>
            <p className='mb-4 text-gray-600'>{message}</p>
          </div>
        )}

        {status === 'success' && (
          <div className='flex flex-col items-center'>
            <CheckCircle className='mb-4 h-16 w-16 text-green-500' />
            <h2 className='mb-4 text-2xl font-bold'>Success!</h2>
            <p className='mb-4 text-gray-600'>{message}</p>
            <p className='mb-4 text-sm text-gray-500'>
              This window will close automatically...
            </p>
            <Button onClick={handleClose} className='mt-4'>
              Close Window
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className='flex flex-col items-center'>
            <AlertCircle className='mb-4 h-16 w-16 text-red-500' />
            <h2 className='mb-4 text-2xl font-bold'>
              {message.includes('cancelled') ? 'Authorization Cancelled' : 'Connection Failed'}
            </h2>
            <p className='mb-4 text-gray-600'>{message}</p>
            {!message.includes('cancelled') && (
              <div className='mb-4 max-w-full overflow-auto rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800'>
                <p>Please try again or contact support if the issue persists.</p>
              </div>
            )}
            <Button onClick={handleClose} className='mt-4'>
              Close Window
            </Button>
          </div>
        )}
      </div>
    </div>
  )
}
